import { type Stripe, loadStripe } from '@stripe/stripe-js';
import { useCallback, useEffect, useRef, useState } from 'react';
import { CustomerApiError, packsApi } from '../customer_api';
import { formatMoney } from '../lib/format';

/**
 * PayPackPanel — Stripe.js Payment Element wrapper for a single daycare
 * pack purchase (Plan 5 M7).
 *
 * Mirrors PayBookingPanel's lifecycle but calls POST /api/customer/packs
 * instead of /api/customer/bookings/:id/pay.
 *
 * Flow:
 *   1. Parent passes `offeringId` + the offering's price/currency for display.
 *   2. beginPayment: POST /api/customer/packs → { client_secret, payment_intent_id }.
 *   3. loadStripe(publishableKey, { stripeAccount }) + mount PaymentElement.
 *   4. submitPayment: stripe.confirmPayment with redirect:'if_required'.
 *   5. onPaid() on success; onError(msg) on failure.
 */

interface Props {
  offeringId: string;
  /** Offering price in minor units — shown in the "Pay now £X.XX" button label. */
  amountMinor: number;
  currency: string;
  /** Platform publishable key (VITE_STRIPE_PUBLISHABLE_KEY). */
  publishableKey: string;
  /** Tenant's Stripe connected account id (from GET /api/customer/packs response). */
  stripeAccountId: string;
  onPaid: () => void;
  onError?: (message: string) => void;
  onCancel?: () => void;
}

type Phase = 'idle' | 'creating_intent' | 'element_ready' | 'submitting' | 'paid' | 'error';

export function PayPackPanel({
  offeringId,
  amountMinor,
  currency,
  publishableKey,
  stripeAccountId,
  onPaid,
  onError,
  onCancel,
}: Props) {
  const [phase, setPhase] = useState<Phase>('idle');
  const [errorMsg, setErrorMsg] = useState<string | null>(null);
  const [clientSecret, setClientSecret] = useState<string | null>(null);

  const stripeRef = useRef<Stripe | null>(null);
  // biome-ignore lint/suspicious/noExplicitAny: StripeElements isn't worth re-typing here.
  const elementsRef = useRef<any>(null);
  const mountNodeRef = useRef<HTMLDivElement | null>(null);

  const fail = useCallback(
    (msg: string) => {
      setPhase('error');
      setErrorMsg(msg);
      onError?.(msg);
    },
    [onError],
  );

  const beginPayment = useCallback(async () => {
    if (phase === 'creating_intent' || phase === 'submitting') return;
    setPhase('creating_intent');
    setErrorMsg(null);

    let purchaseResponse: { client_secret: string; payment_intent_id: string };
    try {
      purchaseResponse = await packsApi.purchase(offeringId);
    } catch (err) {
      const msg =
        err instanceof CustomerApiError
          ? `Could not start payment (HTTP ${err.status}).`
          : 'Could not start payment. Please try again.';
      fail(msg);
      return;
    }

    setClientSecret(purchaseResponse.client_secret);

    let stripe: Stripe | null;
    try {
      stripe = await loadStripe(publishableKey, { stripeAccount: stripeAccountId });
    } catch {
      fail('Could not load Stripe. Please refresh and try again.');
      return;
    }
    if (!stripe) {
      fail('Stripe failed to initialise. Please refresh and try again.');
      return;
    }
    stripeRef.current = stripe;
    setPhase('element_ready');
  }, [offeringId, publishableKey, stripeAccountId, fail, phase]);

  useEffect(() => {
    if (phase !== 'element_ready') return;
    if (!clientSecret || !stripeRef.current || !mountNodeRef.current) return;

    const elements = stripeRef.current.elements({
      clientSecret,
      appearance: { theme: 'stripe' },
    });
    elementsRef.current = elements;
    const paymentElement = elements.create('payment');
    paymentElement.mount(mountNodeRef.current);

    return () => {
      try {
        paymentElement.unmount();
      } catch {
        // ignore — happens if the element was already torn down.
      }
    };
  }, [clientSecret, phase]);

  const submitPayment = useCallback(async () => {
    if (!stripeRef.current || !elementsRef.current) {
      fail('Payment not ready. Please try again.');
      return;
    }
    setPhase('submitting');
    setErrorMsg(null);

    const { error, paymentIntent } = await stripeRef.current.confirmPayment({
      elements: elementsRef.current,
      redirect: 'if_required',
      confirmParams: {
        return_url: `${window.location.origin}/account/packs`,
      },
    });

    if (error) {
      fail(error.message ?? 'Payment failed. Please try again.');
      return;
    }
    if (!paymentIntent) {
      fail('Payment did not complete. Please try again.');
      return;
    }

    setPhase('paid');
    onPaid();
  }, [fail, onPaid]);

  if (phase === 'paid') {
    return (
      <div
        style={{
          marginTop: '1rem',
          padding: '1rem 1.25rem',
          borderRadius: '12px',
          border: '1px solid oklch(0.55 0.12 150 / 0.30)',
          background: 'oklch(0.55 0.12 150 / 0.08)',
          color: 'oklch(0.36 0.12 150)',
          fontSize: '0.9rem',
        }}
      >
        Payment received. Your pack will appear above shortly.
      </div>
    );
  }

  const amountLabel = formatMoney(amountMinor, currency);

  return (
    <div
      style={{
        marginTop: '1.5rem',
        padding: '1.25rem',
        background: 'var(--surface, white)',
        borderRadius: '12px',
        border: '1px solid var(--border, oklch(0.915 0.005 80))',
      }}
    >
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          marginBottom: '0.75rem',
        }}
      >
        <div
          style={{
            fontSize: '0.7rem',
            textTransform: 'uppercase',
            letterSpacing: '0.14em',
            color: 'var(--muted-foreground, oklch(0.52 0.008 75))',
          }}
        >
          Payment
        </div>
        {onCancel && phase !== 'submitting' && (
          <button
            type="button"
            onClick={onCancel}
            style={{
              padding: '0.25rem 0.5rem',
              borderRadius: '6px',
              border: '1px solid var(--border, oklch(0.915 0.005 80))',
              background: 'transparent',
              fontSize: '0.78rem',
              cursor: 'pointer',
              color: 'var(--muted-foreground, oklch(0.52 0.008 75))',
            }}
          >
            Cancel
          </button>
        )}
      </div>

      {phase === 'idle' && (
        <button
          type="button"
          onClick={beginPayment}
          style={{
            display: 'block',
            width: '100%',
            padding: '0.85rem 1rem',
            borderRadius: '10px',
            border: 'none',
            background: 'var(--primary, oklch(0.43 0.18 263))',
            color: 'white',
            fontSize: '0.95rem',
            fontWeight: 500,
            cursor: 'pointer',
          }}
        >
          Pay {amountLabel}
        </button>
      )}

      {phase === 'creating_intent' && (
        <p
          style={{
            margin: 0,
            fontSize: '0.9rem',
            color: 'var(--muted-foreground, oklch(0.52 0.008 75))',
          }}
        >
          Preparing payment…
        </p>
      )}

      {(phase === 'element_ready' || phase === 'submitting') && (
        <>
          <div ref={mountNodeRef} data-testid="payment-element" />
          <button
            type="button"
            onClick={submitPayment}
            disabled={phase === 'submitting'}
            style={{
              display: 'block',
              width: '100%',
              marginTop: '1rem',
              padding: '0.85rem 1rem',
              borderRadius: '10px',
              border: 'none',
              background: 'var(--primary, oklch(0.43 0.18 263))',
              color: 'white',
              fontSize: '0.95rem',
              fontWeight: 500,
              cursor: phase === 'submitting' ? 'not-allowed' : 'pointer',
              opacity: phase === 'submitting' ? 0.6 : 1,
            }}
          >
            {phase === 'submitting' ? 'Processing…' : `Pay now ${amountLabel}`}
          </button>
        </>
      )}

      {phase === 'error' && (
        <>
          <p
            style={{
              margin: '0 0 0.75rem',
              color: 'var(--destructive, oklch(0.55 0.15 30))',
              fontSize: '0.9rem',
            }}
          >
            {errorMsg ?? 'Payment failed.'}
          </p>
          <button
            type="button"
            onClick={beginPayment}
            style={{
              display: 'block',
              width: '100%',
              padding: '0.75rem 1rem',
              borderRadius: '10px',
              border: '1px solid var(--border, oklch(0.915 0.005 80))',
              background: 'var(--surface, white)',
              color: 'var(--foreground, oklch(0.19 0.012 75))',
              fontSize: '0.9rem',
              fontWeight: 500,
              cursor: 'pointer',
            }}
          >
            Try again
          </button>
        </>
      )}
    </div>
  );
}
